/**
 * GTM dataLayer helpers for registration conversions.
 *
 * Email and phone are the only PII the registration dataLayer push needs to
 * carry, and they must never land in the dataLayer in raw form — GTM
 * containers, tags, and browser extensions all read `window.dataLayer`
 * freely. They are SHA-256 hashed before the push, following Google's
 * enhanced-conversions user-provided data normalization:
 *
 * - email: trimmed and lowercased, with all periods removed from the local
 *   part of `gmail.com` / `googlemail.com` addresses (Google's documented
 *   normalization), then hashed
 * - phone: converted to E.164 (`+55` country code included), then hashed
 *
 * The hashed values are pushed under `user_data.email_address` /
 * `user_data.phone_number`, the documented gtag/GTM shape, so the Tag
 * Manager side can read them with plain Data Layer Variables without
 * re-hashing.
 *
 * Hashing uses the browser-native Web Crypto API (`crypto.subtle`), which is
 * available in every secure context the register page runs in (HTTPS in
 * production, localhost in development).
 */

/** SHA-256 of `input`, hex-encoded (lowercase). */
export async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Normalize an email for Google enhanced-conversions hashing: trim
 * leading/trailing whitespace, lowercase every character, and remove all
 * periods from the local part of `gmail.com` / `googlemail.com` addresses.
 *
 * Google's documented normalization requires the dot stripping because
 * Gmail ignores dots: Google matches the digest against the canonical
 * dotless form of the account, so `john.doe@gmail.com` and
 * `johndoe@gmail.com` must produce the same hash. Dots in other domains
 * are significant and are kept.
 */
export function normalizeEmailForHashing(email: string): string {
  const normalized = email.trim().toLowerCase();
  const at = normalized.lastIndexOf('@');
  if (at === -1) return normalized;
  const local = normalized.slice(0, at);
  const domain = normalized.slice(at + 1);
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    return `${local.replaceAll('.', '')}@${domain}`;
  }
  return normalized;
}

/**
 * Convert a phone value to E.164 (`+5511987654321`) for hashing.
 *
 * The register form stores the national display format — `(11) 98765-4321`,
 * 10-11 digits — so the Brazilian country code is prepended. Values that
 * already carry 12-13 digits are assumed to include a country code and pass
 * through untouched. Anything with fewer than 10 digits is treated as
 * invalid and returns an empty string (the caller then omits the field).
 */
export function normalizePhoneToE164(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10) {
    return '';
  }
  const withCountryCode = digits.length >= 12 ? digits : `55${digits}`;
  return `+${withCountryCode}`;
}

export interface HashedUserData {
  email_address?: string;
  phone_number?: string;
}

/**
 * Hash user contact data into the Google `user_data` shape. Missing or blank
 * values are omitted — no empty-string hashes ever reach the dataLayer.
 */
export async function hashUserData({
  email,
  phone,
}: {
  email?: string;
  phone?: string;
}): Promise<HashedUserData> {
  const normalizedEmail = email ? normalizeEmailForHashing(email) : '';
  const normalizedPhone = phone ? normalizePhoneToE164(phone) : '';

  const userData: HashedUserData = {};
  if (normalizedEmail) {
    userData.email_address = await sha256Hex(normalizedEmail);
  }
  if (normalizedPhone) {
    userData.phone_number = await sha256Hex(normalizedPhone);
  }
  return userData;
}

export interface RegisterSuccessDataLayerParams {
  userId?: string;
  anonymousId?: string;
  email?: string;
  phone?: string;
}

/**
 * Push the `register_success` event to the GTM dataLayer with contact data
 * pre-hashed. Mirrors the client-side RudderStack `Registration Success`
 * event so Tag Manager can track registration conversions on its own pipe.
 * No-ops outside the browser.
 */
export async function pushRegisterSuccessDataLayer({
  userId,
  anonymousId,
  email,
  phone,
}: RegisterSuccessDataLayerParams): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  const userData = await hashUserData({ email, phone });

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'register_success',
    user_id: userId,
    rudderstack_anonymous_id: anonymousId,
    ...(userData.email_address || userData.phone_number
      ? { user_data: userData }
      : {}),
  });
}
